/**
 * blockLibrary.js
 *
 * Catálogo de bloques: qué se puede arrastrar al lienzo y con qué contenido nace.
 *
 * Principio de diseño: NINGÚN bloque nace vacío. Un elemento de texto vacío en el
 * lienzo se ve como un error; uno con texto de ejemplo se ve como algo que se
 * puede editar. Es la diferencia entre "no sé qué hacer" y "borro esto y escribo
 * lo mío" — y es exactamente lo que hace que el editor se sienta usable a los
 * treinta segundos.
 */

import { uid } from './builderModel';

/* ------------------------------------------------------------------ *
 * Elementos sueltos
 * ------------------------------------------------------------------ */

export const ELEMENT_LIBRARY = [
  {
    type: 'heading', label: 'Título', icon: 'T', group: 'Básico',
    make: () => ({
      id: uid('hea'), type: 'heading', props: { text: 'Un título que convence', tag: 'h2' },
      style: { desktop: { fontSize: 34, fontWeight: 700, color: 'var(--c-text)', marginBottom: 12 }, mobile: { fontSize: 26 } },
      advanced: {}
    })
  },
  {
    type: 'text', label: 'Texto', icon: '¶', group: 'Básico',
    make: () => ({
      id: uid('tex'), type: 'text',
      props: { html: 'Escribe aquí lo que tu cliente necesita saber. Haz clic sobre este texto y edítalo directamente.' },
      style: { desktop: { fontSize: 17, lineHeight: 1.7, color: 'var(--c-muted)', marginBottom: 16 } },
      advanced: {}
    })
  },
  {
    type: 'button', label: 'Botón', icon: '▭', group: 'Básico',
    make: () => ({
      id: uid('btn'), type: 'button',
      props: { label: 'Quiero más información', action: 'anchor', anchor: 'contacto', variant: 'solid' },
      style: {
        desktop: {
          fontSize: 16, fontWeight: 600, color: '#FFFFFF', backgroundColor: 'var(--c-primary)',
          paddingTop: 14, paddingBottom: 14, paddingLeft: 28, paddingRight: 28, borderRadius: 'var(--radius)'
        }
      },
      advanced: {}
    })
  },
  {
    type: 'image', label: 'Imagen', icon: '▣', group: 'Básico',
    make: () => ({
      id: uid('img'), type: 'image',
      props: { src: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=70', alt: '' },
      style: { desktop: { width: '100%', borderRadius: 'var(--radius)', objectFit: 'cover' } },
      advanced: {}
    })
  },
  {
    type: 'video', label: 'Video', icon: '▶', group: 'Básico',
    make: () => ({
      id: uid('vid'), type: 'video', props: { src: '' }, style: {}, advanced: {}
    })
  },
  {
    type: 'icon', label: 'Icono', icon: '★', group: 'Básico',
    make: () => ({
      id: uid('ico'), type: 'icon', props: { name: 'star', size: 40 },
      style: { desktop: { color: 'var(--c-primary)', marginBottom: 12 } }, advanced: {}
    })
  },
  {
    type: 'divider', label: 'Separador', icon: '—', group: 'Básico',
    make: () => ({
      id: uid('div'), type: 'divider', props: { thickness: 1 },
      style: { desktop: { color: 'var(--c-border)', marginTop: 16, marginBottom: 16 } }, advanced: {}
    })
  },
  {
    type: 'spacer', label: 'Espacio', icon: '⇕', group: 'Básico',
    make: () => ({ id: uid('spa'), type: 'spacer', props: { height: 40 }, style: {}, advanced: {} })
  },
  {
    type: 'list', label: 'Lista', icon: '≔', group: 'Contenido',
    make: () => ({
      id: uid('lis'), type: 'list',
      props: { icon: 'check', items: ['Primer beneficio concreto', 'Segundo beneficio concreto', 'Tercer beneficio concreto'] },
      style: { desktop: { fontSize: 16, color: 'var(--c-text)' } }, advanced: {}
    })
  },
  {
    type: 'testimonial', label: 'Testimonio', icon: '❝', group: 'Contenido',
    make: () => ({
      id: uid('tst'), type: 'testimonial',
      props: { quote: 'Resolvieron en una semana lo que llevábamos meses postergando.', author: 'Cliente satisfecho', role: 'Gerente', stars: 5 },
      style: {}, advanced: {}
    })
  },
  {
    type: 'pricing', label: 'Plan / precio', icon: '$', group: 'Contenido',
    make: () => ({
      id: uid('pri'), type: 'pricing',
      props: {
        plan: 'Plan Profesional', price: '$99', period: '/mes',
        features: ['Todo lo esencial incluido', 'Soporte por WhatsApp', 'Sin permanencia'],
        ctaLabel: 'Contratar', ctaUrl: '#contacto', highlighted: false
      },
      style: {}, advanced: {}
    })
  },
  {
    type: 'faq', label: 'Preguntas frecuentes', icon: '?', group: 'Contenido',
    make: () => ({
      id: uid('faq'), type: 'faq',
      props: {
        items: [
          { q: '¿Cómo empiezo?', a: 'Déjanos tus datos y te contactamos el mismo día.' },
          { q: '¿Qué formas de pago aceptan?', a: 'Efectivo, transferencia y tarjeta.' }
        ]
      },
      style: {}, advanced: {}
    })
  },
  {
    type: 'countdown', label: 'Cuenta regresiva', icon: '⏱', group: 'Contenido',
    make: () => ({
      id: uid('cou'), type: 'countdown',
      props: { target: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16), expiredText: 'La promoción terminó' },
      style: { desktop: { color: 'var(--c-text)', textAlign: 'center' } }, advanced: {}
    })
  },
  {
    type: 'form', label: 'Formulario', icon: '✉', group: 'Conversión',
    make: () => ({
      id: uid('for'), type: 'form',
      props: {
        title: 'Déjanos tus datos',
        submitLabel: 'Enviar',
        successMessage: '¡Gracias! Te contactamos pronto.',
        tagName: 'Lead Web',
        fields: [
          { key: 'nombre', label: 'Nombre', type: 'text', required: true, mapTo: 'name', placeholder: 'Tu nombre' },
          { key: 'telefono', label: 'WhatsApp', type: 'tel', required: true, mapTo: 'phone', placeholder: '09XXXXXXXX' },
          { key: 'mensaje', label: '¿En qué te ayudamos?', type: 'textarea', required: false, mapTo: 'notes' }
        ]
      },
      style: {
        desktop: {
          backgroundColor: 'var(--c-bg)', borderRadius: 'var(--radius)',
          paddingTop: 28, paddingBottom: 28, paddingLeft: 24, paddingRight: 24,
          boxShadow: '0 10px 30px rgba(0,0,0,.08)'
        }
      },
      advanced: {}
    })
  },
  {
    type: 'booking', label: 'Agendar cita', icon: '📅', group: 'Conversión',
    make: () => ({ id: uid('boo'), type: 'booking', props: { bookingSlug: '', height: 720 }, style: {}, advanced: {} })
  },
  {
    type: 'social', label: 'Redes sociales', icon: '◎', group: 'Conversión',
    make: () => ({
      id: uid('soc'), type: 'social',
      props: { links: [{ network: 'whatsapp', url: '' }, { network: 'instagram', url: '' }, { network: 'facebook', url: '' }] },
      style: { desktop: { color: 'var(--c-primary)' } }, advanced: {}
    })
  },
  {
    type: 'map', label: 'Mapa', icon: '⌖', group: 'Conversión',
    make: () => ({ id: uid('map'), type: 'map', props: { query: '', height: 320 }, style: {}, advanced: {} })
  },
  {
    type: 'html', label: 'HTML propio', icon: '</>', group: 'Avanzado',
    make: () => ({ id: uid('htm'), type: 'html', props: { code: '<!-- tu código aquí -->' }, style: {}, advanced: {} })
  }
];

export const ELEMENT_GROUPS = ['Básico', 'Contenido', 'Conversión', 'Avanzado'];

export function makeElement(type) {
  const def = ELEMENT_LIBRARY.find((e) => e.type === type);
  return def ? def.make() : null;
}

/* ------------------------------------------------------------------ *
 * Estructuras
 * ------------------------------------------------------------------ */

export function makeColumn(width, children) {
  return {
    id: uid('col'), type: 'column',
    props: { widthDesktop: width, widthMobile: 100 },
    style: {}, advanced: {}, children: children || []
  };
}

export function makeRow(widths, childrenPerColumn) {
  return {
    id: uid('row'), type: 'row', props: {},
    style: { desktop: { gap: 24 } }, advanced: {},
    children: (widths || [100]).map((w, i) => makeColumn(w, (childrenPerColumn || [])[i] || []))
  };
}

export function makeSection(rows, style, props) {
  return {
    id: uid('sec'), type: 'section',
    props: props || {},
    style: style || { desktop: { paddingTop: 64, paddingBottom: 64, paddingLeft: 20, paddingRight: 20 } },
    advanced: {},
    children: rows || [makeRow([100])]
  };
}

/** Estructuras de columnas ofrecidas al arrastrar una fila nueva. */
export const LAYOUT_PRESETS = [
  { label: '1 columna', widths: [100] },
  { label: '2 columnas', widths: [50, 50] },
  { label: '3 columnas', widths: [33.33, 33.33, 33.33] },
  { label: '4 columnas', widths: [25, 25, 25, 25] },
  { label: '1/3 + 2/3', widths: [33.33, 66.67] },
  { label: '2/3 + 1/3', widths: [66.67, 33.33] }
];

/* ------------------------------------------------------------------ *
 * Secciones prearmadas
 *
 * Esto es lo que la gente realmente usa: nadie construye un hero columna por
 * columna si puede soltar uno ya hecho y cambiarle las palabras.
 * ------------------------------------------------------------------ */

const IMG = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=70';

const el = (type, props, style) => {
  const base = makeElement(type);
  return Object.assign({}, base, {
    props: Object.assign({}, base.props, props || {}),
    style: style || base.style
  });
};

export const SECTION_PRESETS = [
  {
    name: 'Hero dividido',
    group: 'Encabezados',
    make: () => makeSection([
      makeRow([55, 45], [
        [
          el('heading', { text: 'Tu propuesta de valor en una frase', tag: 'h1' }, {
            desktop: { fontSize: 50, fontWeight: 700, color: 'var(--c-text)', lineHeight: 1.15, marginBottom: 16 },
            mobile: { fontSize: 30 }
          }),
          el('text', { html: 'Explica en dos líneas qué hace tu negocio y por qué alguien debería elegirte a ti.' }),
          el('button', { label: 'Empezar ahora' })
        ],
        [el('image', { src: IMG })]
      ])
    ], {
      desktop: { paddingTop: 80, paddingBottom: 80, paddingLeft: 20, paddingRight: 20, backgroundColor: 'var(--c-surface)' }
    }, { anchor: 'inicio' })
  },
  {
    name: 'Hero centrado con degradado',
    group: 'Encabezados',
    make: () => makeSection([
      makeRow([100], [[
        el('heading', { text: 'La oferta que estabas esperando', tag: 'h1' }, {
          desktop: { fontSize: 54, fontWeight: 700, color: '#FFFFFF', textAlign: 'center', marginBottom: 16 },
          mobile: { fontSize: 30 }
        }),
        el('text', { html: 'Una línea de apoyo que refuerza la promesa principal.' }, {
          desktop: { fontSize: 19, color: 'rgba(255,255,255,.9)', textAlign: 'center', marginBottom: 28 }
        }),
        el('button', { label: 'Quiero aprovecharla' })
      ]])
    ], {
      desktop: {
        paddingTop: 100, paddingBottom: 100, paddingLeft: 20, paddingRight: 20,
        background: 'linear-gradient(135deg, var(--c-primary), var(--c-secondary))', textAlign: 'center'
      }
    }, { anchor: 'inicio' })
  },
  {
    name: '3 beneficios',
    group: 'Contenido',
    make: () => makeSection([
      makeRow([100], [[el('heading', { text: 'Por qué elegirnos' }, {
        desktop: { fontSize: 36, fontWeight: 700, textAlign: 'center', color: 'var(--c-text)', marginBottom: 32 }
      })]]),
      makeRow([33.33, 33.33, 33.33], [
        [el('icon', { name: 'star' }), el('heading', { text: 'Beneficio uno', tag: 'h3' }, { desktop: { fontSize: 20, fontWeight: 600, marginBottom: 8 } }), el('text', { html: 'Explica el beneficio en una o dos líneas.' })],
        [el('icon', { name: 'clock' }), el('heading', { text: 'Beneficio dos', tag: 'h3' }, { desktop: { fontSize: 20, fontWeight: 600, marginBottom: 8 } }), el('text', { html: 'Explica el beneficio en una o dos líneas.' })],
        [el('icon', { name: 'heart' }), el('heading', { text: 'Beneficio tres', tag: 'h3' }, { desktop: { fontSize: 20, fontWeight: 600, marginBottom: 8 } }), el('text', { html: 'Explica el beneficio en una o dos líneas.' })]
      ])
    ])
  },
  {
    name: 'Texto + imagen',
    group: 'Contenido',
    make: () => makeSection([
      makeRow([50, 50], [
        [el('heading', { text: 'Cuenta tu historia' }), el('text', { html: 'Un párrafo que explique quién eres, desde cuándo y qué te diferencia.' }), el('button', { label: 'Conocer más' })],
        [el('image', { src: IMG })]
      ])
    ])
  },
  {
    name: 'Precios (3 planes)',
    group: 'Conversión',
    make: () => makeSection([
      makeRow([100], [[el('heading', { text: 'Planes' }, {
        desktop: { fontSize: 36, fontWeight: 700, textAlign: 'center', marginBottom: 32, color: 'var(--c-text)' }
      })]]),
      makeRow([33.33, 33.33, 33.33], [
        [el('pricing', { plan: 'Inicial', price: '$29' })],
        [el('pricing', { plan: 'Profesional', price: '$99', highlighted: true })],
        [el('pricing', { plan: 'Empresarial', price: '$199' })]
      ])
    ])
  },
  {
    name: 'Formulario de contacto',
    group: 'Conversión',
    make: () => makeSection([
      makeRow([50, 50], [
        [el('heading', { text: 'Conversemos' }), el('text', { html: 'Déjanos tus datos y te contactamos hoy mismo por WhatsApp.' })],
        [el('form')]
      ])
    ], {
      desktop: { paddingTop: 72, paddingBottom: 72, paddingLeft: 20, paddingRight: 20, backgroundColor: 'var(--c-surface)' }
    }, { anchor: 'contacto' })
  },
  {
    name: 'Agendar cita',
    group: 'Conversión',
    make: () => makeSection([
      makeRow([100], [[
        el('heading', { text: 'Agenda tu cita' }, {
          desktop: { fontSize: 36, fontWeight: 700, textAlign: 'center', marginBottom: 24, color: 'var(--c-text)' }
        }),
        el('booking')
      ]])
    ], null, { anchor: 'agenda' })
  },
  {
    name: 'Testimonios',
    group: 'Confianza',
    make: () => makeSection([
      makeRow([100], [[el('heading', { text: 'Lo que dicen nuestros clientes' }, {
        desktop: { fontSize: 36, fontWeight: 700, textAlign: 'center', marginBottom: 32, color: 'var(--c-text)' }
      })]]),
      makeRow([50, 50], [[el('testimonial')], [el('testimonial', { quote: 'Responden rápido y explican todo en palabras simples.', author: 'Otro cliente' })]])
    ], {
      desktop: { paddingTop: 72, paddingBottom: 72, paddingLeft: 20, paddingRight: 20, backgroundColor: 'var(--c-surface)' }
    })
  },
  {
    name: 'Preguntas frecuentes',
    group: 'Confianza',
    make: () => makeSection([
      makeRow([100], [[el('heading', { text: 'Preguntas frecuentes' }, {
        desktop: { fontSize: 36, fontWeight: 700, textAlign: 'center', marginBottom: 24, color: 'var(--c-text)' }
      }), el('faq')]])
    ], null, { anchor: 'faq' })
  },
  {
    name: 'Pie de página',
    group: 'Cierre',
    make: () => makeSection([
      makeRow([60, 40], [
        [el('text', { html: '<strong>Tu Negocio</strong><br/>Dirección · Horario de atención' }, {
          desktop: { fontSize: 14, color: '#FFFFFF', lineHeight: 1.7 }
        })],
        [el('social', {}, { desktop: { color: '#FFFFFF', textAlign: 'right' } })]
      ])
    ], {
      desktop: { paddingTop: 44, paddingBottom: 44, paddingLeft: 20, paddingRight: 20, backgroundColor: 'var(--c-text)' }
    }, { anchor: 'footer' })
  },
  {
    name: 'Mapa y ubicación',
    group: 'Cierre',
    make: () => makeSection([
      makeRow([40, 60], [
        [el('heading', { text: 'Dónde estamos' }), el('text', { html: 'Dirección exacta y referencias para llegar.' })],
        [el('map')]
      ])
    ])
  }
];

export const SECTION_GROUPS = ['Encabezados', 'Contenido', 'Conversión', 'Confianza', 'Cierre'];

export const ICON_NAMES = [
  'star', 'clock', 'heart', 'shield', 'chart', 'users', 'gift', 'check',
  'phone', 'mail', 'whatsapp', 'instagram', 'facebook', 'tiktok'
];
